import RequestService, {
    RequestServiceResponse,
} from '@tcc-online/request-service';
import { GetThunkAPI } from '@reduxjs/toolkit/dist/createAsyncThunk';
import { NotificationActions } from '../../common/actions/NotificationActions';
import { isSuccessfulResponse } from '../../helpers';
import ProjectInvitation from '../types/invitation/ProjectInvitation';
import ProjectInvitationParticipant from '../types/invitation/ProjectInvitationParticipant';
import ProjectInvitationRecap from '../types/invitation/ProjectInvitationRecap';

const createBaseUrl = (
    productId: number,
    projectId: number,
    append: string = '',
) => {
    return `/admin/products/${productId}/projects/${projectId}/invitations${
        append ? `/${append}` : ''
    }`;
};

const AdminInviteService = {
    async createInvite(
        payload: { productId: number; projectId: number; name: string },
        thunkApi: GetThunkAPI<any>,
    ): Promise<RequestServiceResponse<any, { name: string }>> {
        const { productId, projectId, name } = payload;
        try {
            return await RequestService.post<any>(
                createBaseUrl(productId, projectId),
                {
                    name,
                },
            );
        } catch (e) {
            thunkApi.dispatch(
                NotificationActions.error('nepodarilo se vytvorit pozvanku'),
            ); // TODO: translate

            throw e;
        }
    },
    async getInvite(
        payload: {
            productId: number;
            projectId: number;
            inviteId: number;
            locale?: string;
        },
        thunkApi: GetThunkAPI<any>,
    ): Promise<RequestServiceResponse<ProjectInvitation>> {
        const { productId, projectId, inviteId, locale } = payload;

        try {
            return await RequestService.get<any>(
                createBaseUrl(
                    productId,
                    projectId,
                    `${inviteId.toString()}${locale ? `?locale=${locale}` : ''}`,
                ),
            );
        } catch (e) {
            thunkApi.dispatch(
                NotificationActions.error('nepodarilo se ziskat pozvanku'),
            ); // TODO: translate

            throw e;
        }
    },
    async getInvites(
        payload: {
            productId: number;
            projectId: number;
            limit?: number;
        },
        thunkApi: GetThunkAPI<any>,
    ): Promise<RequestServiceResponse<ProjectInvitation[]>> {
        const { productId, projectId, limit = 100 } = payload;

        try {
            return await RequestService.get<any>(
                createBaseUrl(productId, projectId),
                { limit },
            );
        } catch (e) {
            thunkApi.dispatch(
                NotificationActions.error('nepodarilo se ziskat pozvanky'),
            ); // TODO: translate

            throw e;
        }
    },
    async saveInvite(
        payload: {
            productId: number;
            projectId: number;
            inviteId: number;
            multilingual?: boolean;
            subject: string;
            content: string;
            locale?: string;
        },
        thunkApi: GetThunkAPI<any>,
    ): Promise<RequestServiceResponse<any>> {
        const { productId, projectId, inviteId, locale, ...body } = payload;

        const baseUrl = createBaseUrl(
            productId,
            projectId,
            inviteId.toString(),
        );
        const url = locale ? `${baseUrl}?locale=${locale}` : baseUrl;

        try {
            const result = await RequestService.patch<any>(url, body);

            if (isSuccessfulResponse(result.status)) {
                thunkApi.dispatch(
                    NotificationActions.success('pozvanka ulozena'),
                ); // TODO: translate
            }

            return result;
        } catch (e) {
            thunkApi.dispatch(
                NotificationActions.error('nepodarilo se ulozit pozvanku'),
            ); // TODO: translate

            throw e;
        }
    },
    async getParticipants(
        payload: { productId: number; projectId: number; inviteId: number },
        thunkApi: GetThunkAPI<any>,
    ): Promise<RequestServiceResponse<ProjectInvitationParticipant[]>> {
        const { productId, projectId, inviteId } = payload;
        try {
            return await RequestService.get<any>(
                createBaseUrl(
                    productId,
                    projectId,
                    `${inviteId.toString()}/participants`,
                ),
            );
        } catch (e) {
            thunkApi.dispatch(
                NotificationActions.error('nepodarilo se nacist ucastniky'),
            ); // TODO: translate

            throw e;
        }
    },
    async changeParticipantLanguage(
        payload: {
            productId: number;
            projectId: number;
            inviteId: number;
            participantId: number;
            locale: string;
        },
        thunkApi: GetThunkAPI<any>,
    ): Promise<RequestServiceResponse<ProjectInvitationParticipant>> {
        const { productId, projectId, inviteId, participantId, locale } =
            payload;
        try {
            return await RequestService.patch<
                any,
                ProjectInvitationParticipant
            >(
                createBaseUrl(
                    productId,
                    projectId,
                    `${inviteId.toString()}/participants/${participantId}`,
                ),
                { locale },
            );
        } catch (e) {
            thunkApi.dispatch(
                NotificationActions.error('nepodarilo se zmenit jazyk'),
            ); // TODO: translate

            throw e;
        }
    },
    async selectParticipant(
        payload: {
            productId: number;
            projectId: number;
            inviteId: number;
            participantId: number;
        },
        thunkApi: GetThunkAPI<any>,
    ): Promise<RequestServiceResponse<ProjectInvitationParticipant>> {
        const { productId, projectId, inviteId, participantId } = payload;
        try {
            return await RequestService.post<any, ProjectInvitationParticipant>(
                createBaseUrl(
                    productId,
                    projectId,
                    `${inviteId.toString()}/participants`,
                ),
                { id: participantId },
            );
        } catch (e) {
            thunkApi.dispatch(
                NotificationActions.error('nepodarilo se zmenit jazyk'),
            ); // TODO: translate

            throw e;
        }
    },
    async selectParticipants(
        payload: {
            productId: number;
            projectId: number;
            inviteId: number;
            participantIds: { id: number }[];
        },
        thunkApi: GetThunkAPI<any>,
    ): Promise<RequestServiceResponse<ProjectInvitationParticipant[]>> {
        const { productId, projectId, inviteId, participantIds } = payload;
        try {
            return await RequestService.patch<
                any,
                ProjectInvitationParticipant[]
            >(
                createBaseUrl(
                    productId,
                    projectId,
                    `${inviteId.toString()}/participants`,
                ),
                participantIds,
            );
        } catch (e) {
            thunkApi.dispatch(
                NotificationActions.error('nepodarilo se priudat ucastniky'),
            ); // TODO: translate

            throw e;
        }
    },
    async deselectParticipant(
        payload: {
            productId: number;
            projectId: number;
            inviteId: number;
            participantId: number;
        },
        thunkApi: GetThunkAPI<any>,
    ): Promise<RequestServiceResponse<ProjectInvitationParticipant>> {
        const { productId, projectId, inviteId, participantId } = payload;
        try {
            return await RequestService.delete(
                createBaseUrl(
                    productId,
                    projectId,
                    `${inviteId.toString()}/participants/${participantId}`,
                ),
            );
        } catch (e) {
            thunkApi.dispatch(
                NotificationActions.error('nepodarilo se odstranit ucastnika'),
            ); // TODO: translate

            throw e;
        }
    },
    async deselectParticipants(
        payload: {
            productId: number;
            projectId: number;
            inviteId: number;
        },
        thunkApi: GetThunkAPI<any>,
    ): Promise<RequestServiceResponse<ProjectInvitationParticipant>> {
        const { productId, projectId, inviteId } = payload;
        try {
            return await RequestService.patch<
                any,
                ProjectInvitationParticipant
            >(
                createBaseUrl(
                    productId,
                    projectId,
                    `${inviteId.toString()}/participants`,
                ),
                [],
            );
        } catch (e) {
            thunkApi.dispatch(
                NotificationActions.error('nepodarilo se odstranit ucastniky'),
            ); // TODO: translate

            throw e;
        }
    },
    async changeName(
        payload: {
            productId: number;
            projectId: number;
            inviteId: number;
            name: string;
        },
        thunkApi: GetThunkAPI<any>,
    ): Promise<RequestServiceResponse<any>> {
        const { productId, projectId, inviteId, name } = payload;

        try {
            return await RequestService.patch<any>(
                createBaseUrl(productId, projectId, inviteId.toString()),
                { name },
            );
        } catch (e) {
            thunkApi.dispatch(
                NotificationActions.error(
                    'nepodarilo se ulozit nazev pozvanky',
                ),
            ); // TODO: translate

            throw e;
        }
    },
    async getRecaps(
        payload: { productId: number; projectId: number; inviteId: number },
        thunkApi: GetThunkAPI<any>,
    ): Promise<RequestServiceResponse<ProjectInvitationRecap[]>> {
        const { productId, projectId, inviteId } = payload;
        try {
            return await RequestService.get<any>(
                createBaseUrl(
                    productId,
                    projectId,
                    `${inviteId.toString()}/recaps`,
                ),
            );
        } catch (e) {
            thunkApi.dispatch(
                NotificationActions.error('nepodarilo se nacist rekapitulace'),
            ); // TODO: translate

            throw e;
        }
    },
    async setMultilingual(
        payload: {
            productId: number;
            projectId: number;
            inviteId: number;
            multilingual: boolean;
        },
        thunkApi: GetThunkAPI<any>,
    ): Promise<RequestServiceResponse<any>> {
        const { productId, projectId, inviteId, multilingual } = payload;

        try {
            return await RequestService.patch<any>(
                createBaseUrl(productId, projectId, inviteId.toString()),
                { multilingual },
            );
        } catch (e) {
            thunkApi.dispatch(
                NotificationActions.error('nepodarilo se ulozit pozvanku'),
            ); // TODO: translate

            throw e;
        }
    },
    async sendInvite(
        payload: {
            productId: number;
            projectId: number;
            inviteId: number;
        },
        thunkApi: GetThunkAPI<any>,
    ): Promise<RequestServiceResponse<any>> {
        const { productId, projectId, inviteId } = payload;

        try {
            const result = await RequestService.patch<any>(
                createBaseUrl(productId, projectId, inviteId.toString()),
                { state: 1 },
            );

            if (isSuccessfulResponse(result.status)) {
                thunkApi.dispatch(
                    NotificationActions.success('pozvanka odeslana'),
                ); // TODO: translate
            }

            return result;
        } catch (e) {
            thunkApi.dispatch(
                NotificationActions.error('nepodarilo se odeslat pozvanku'),
            ); // TODO: translate

            throw e;
        }
    },
};

export default AdminInviteService;
